import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { ItfDatabase } from '../../../models/itf-database.model';
import { ItfPago } from '../../../models/itf-pago.model';
import { Graduacion } from '../../../models/graduacion.model';
import { ItfDatabaseService } from '../../../services/itf-database.service';
import { SharedService } from '../../../services/shared.service';

@Component({
  selector: 'app-itf-database-detail',
  templateUrl: './itf-database-detail.component.html',
  styleUrls: ['./itf-database-detail.component.css']
})
export class ItfDatabaseDetailComponent implements OnInit {
  @Input() itfRecord!: ItfDatabase;
  @Input() isNew: boolean = false;
  @Output() cancelEdit = new EventEmitter<void>();

  // Edit mode
  isEditMode: boolean = false;
  editedRecord: ItfDatabase | null = null;
  
  // Graduaciones
  graduaciones: Graduacion[] = [];
  gupList: Array<{ gup: number; graduacion: Graduacion }> = [];
  danList: Array<{ dan: number; graduacion: Graduacion }> = [];
  gradoTipo: 'GUP' | 'DAN' | null = null;

  // Payment management
  showPaymentModal: boolean = false;
  paymentHistory: ItfPago[] = [];
  showPaymentHistory: boolean = false;

  // UI state
  isSaving: boolean = false;
  error: string | null = null;
  successMessage: string | null = null;

  // Validation
  validationErrors: { [key: string]: string } = {};


  constructor(
    private itfDatabaseService: ItfDatabaseService,
    private sharedService: SharedService
  ) {}

    ngOnInit(): void {
    this.sharedService.graduaciones$.subscribe(graduaciones => {
      this.graduaciones = graduaciones;
      this.buildGraduacionLists();
      this.setInitialGrado();
    });

    if (this.isNew) {
      this.isEditMode = true;
      this.editedRecord = this.cloneRecord(this.itfRecord);
    } else {
      this.loadPaymentHistory();
    }
  }

  // ============================================================================
  // GRADUACION MANAGEMENT
  // ============================================================================

  private buildGraduacionLists(): void {
    // Build GUP list (10 to 1) with graduation info
    this.gupList = [];
    for (let gup = 10; gup >= 1; gup--) {
      const graduacion = this.graduaciones.find(g => g.gup === gup);
      if (graduacion) {
        this.gupList.push({ gup, graduacion });
      }
    }

    // Build DAN list (1 to 9) with graduation info
    this.danList = [];
    for (let dan = 1; dan <= 9; dan++) {
      const graduacion = this.graduaciones.find(g => g.dan === dan);
      if (graduacion) {
        this.danList.push({ dan, graduacion });
      }
    }
  }

  private setInitialGrado(): void {
    const record = this.isEditMode && this.editedRecord ? this.editedRecord : this.itfRecord;
    if (!record) return;

    // Check if it's GUP or DAN
    if (record.gup !== null && record.gup !== undefined) {
      this.gradoTipo = 'GUP';
    } else if (record.dan !== null && record.dan !== undefined) {
      this.gradoTipo = 'DAN';
    }
  }

  onGradoTipoChange(): void {
    if (!this.editedRecord) return;

    if (this.gradoTipo === 'GUP') {
      // Selected GUP - clear DAN fields
      this.editedRecord.dan = null;
      this.editedRecord.nuCertDan = null;
      // Set default GUP if none selected
      if (this.editedRecord.gup === null) {
        this.editedRecord.gup = 10;
      }
    } else if (this.gradoTipo === 'DAN') {
      // Selected DAN - clear GUP field
      this.editedRecord.gup = null;
      // Set default DAN if none selected
      if (this.editedRecord.dan === null) {
        this.editedRecord.dan = 1;
      }
      // Set default certificate format
      if (!this.editedRecord.nuCertDan || this.editedRecord.nuCertDan.trim() === '') {
        this.editedRecord.nuCertDan = `AR-${this.editedRecord.dan}-`;
      }
    }

    delete this.validationErrors['grado'];
    delete this.validationErrors['nuCertDan'];
  }

  onGupChange(): void {
    // Just clear validation error
    delete this.validationErrors['grado'];
  }

  onDanChange(): void {
    if (!this.editedRecord || !this.editedRecord.dan) return;

    // Update certificate format when DAN number changes
    // Only update if it's empty or follows the AR-X- pattern
    const currentCert = this.editedRecord.nuCertDan || '';
    const isDefaultFormat = currentCert === '' || /^AR-\d+-$/.test(currentCert);
    
    if (isDefaultFormat) {
      this.editedRecord.nuCertDan = `AR-${this.editedRecord.dan}-`;
    }

    delete this.validationErrors['grado'];
    delete this.validationErrors['nuCertDan'];
  }

  getGraduacionDisplay(): string {
    const record = this.isEditMode ? this.editedRecord : this.itfRecord;
    if (!record) return 'N/A';

    if (record.gup !== null && record.gup !== undefined) {
      const grad = this.graduaciones.find(g => g.gup === record.gup);
      return grad ? grad.nombre : `GUP ${record.gup}`;
    } else if (record.dan !== null && record.dan !== undefined) {
      const grad = this.graduaciones.find(g => g.dan === record.dan);
      return grad ? `${grad.nombre} - Cert: ${record.nuCertDan || 'N/A'}` : `${record.dan}° DAN`;
    }
    
    return 'N/A';
  }

  // ============================================================================
  // EDIT MODE
  // ============================================================================

  enableEdit(): void {
    this.isEditMode = true;
    this.editedRecord = this.cloneRecord(this.itfRecord);
    this.setInitialGrado();
    this.error = null;
    this.validationErrors = {};
  }

  cancelEditMode(): void {
    if (this.isNew) {
      this.cancelEdit.emit();
    } else {
      this.isEditMode = false;
      this.editedRecord = null;
      this.gradoTipo = null;
      this.error = null;
      this.validationErrors = {};
    }
  }

  private cloneRecord(record: ItfDatabase): ItfDatabase {
    return new ItfDatabase(
      record.id,
      record.nroLibreta,
      record.nombre,
      record.apellido,
      record.fcNacimiento,
      record.sexo,
      record.gup,
      record.nuCertDan,
      record.dan,
      record.ngb,
      record.nacionalidad,
      record.emailNgb,
      record.matriculaFcPago,
      record.matriculaAnio,
      record.matriculaVigente,
      record.matriculaMonto,
      record.activo,
      record.createdAt,
      record.updatedAt,
      record.createdBy,
      record.estudiante
    );
  }

  // ============================================================================
  // SAVE/UPDATE
  // ============================================================================

  saveRecord(): void {
    if (!this.editedRecord) return;

    // ✅ DEBUG - Log what we're about to send
    console.log('=== SAVE RECORD DEBUG ===');
    console.log('editedRecord.nombre:', this.editedRecord.nombre);
    console.log('editedRecord.emailNgb:', this.editedRecord.emailNgb);
    console.log('editedRecord object:', this.editedRecord);

    if (!this.validateRecord(this.editedRecord)) {
      this.error = 'Por favor, corrija los errores en el formulario';
      return;
    }

    this.isSaving = true;
    this.error = null;

    if (this.isNew) {
      // Build complete payload for creation
      const createPayload = {
        nroLibreta: this.editedRecord.nroLibreta,
        nombre: this.editedRecord.nombre,
        apellido: this.editedRecord.apellido,
        fcNacimiento: this.editedRecord.fcNacimiento,
        sexo: this.editedRecord.sexo,
        gup: this.editedRecord.gup,
        nuCertDan: this.editedRecord.nuCertDan,
        dan: this.editedRecord.dan,
        ngb: this.editedRecord.ngb,
        nacionalidad: this.editedRecord.nacionalidad,
        emailNgb: this.editedRecord.emailNgb,
        createdBy: this.editedRecord.createdBy
      };

      // createPayload, not this.editedRecord
      this.itfDatabaseService.create(createPayload as any).subscribe(
        (created) => {
          this.itfRecord = created;
          this.isNew = false;
          this.isEditMode = false;
          this.editedRecord = null;
          this.gradoTipo = null;
          this.isSaving = false;
          this.successMessage = 'Registro ITF creado exitosamente';
          setTimeout(() => this.successMessage = null, 3000);
        },
        (err) => {
          this.error = err.error?.error || 'Error al crear el registro';
          this.isSaving = false;
        }
      );
    } else {
      // Build update payload
      const updatePayload = {
        nombre: this.editedRecord.nombre,
        apellido: this.editedRecord.apellido,
        fcNacimiento: this.editedRecord.fcNacimiento,
        sexo: this.editedRecord.sexo,
        gup: this.editedRecord.gup,
        nuCertDan: this.editedRecord.nuCertDan,
        dan: this.editedRecord.dan,
        ngb: this.editedRecord.ngb,
        nacionalidad: this.editedRecord.nacionalidad,
        emailNgb: this.editedRecord.emailNgb
      };

      // ✅ DEBUG - Log the payload
      console.log('updatePayload:', updatePayload);
      console.log('=== END DEBUG ===');

      // PASS updatePayload
      this.itfDatabaseService.update(this.editedRecord.id!, updatePayload).subscribe(
        (updated) => {
          this.itfRecord = updated;
          this.isEditMode = false;
          this.editedRecord = null;
          this.gradoTipo = null;
          this.isSaving = false;
          this.successMessage = 'Registro ITF actualizado exitosamente';
          setTimeout(() => this.successMessage = null, 3000);
          
          // Reload payment history after update
          this.loadPaymentHistory();
        },
        (err) => {
          this.error = err.error?.error || 'Error al actualizar el registro';
          this.isSaving = false;
        }
      );
    }
  }

  private validateRecord(record: ItfDatabase): boolean {
    this.validationErrors = {};
    let isValid = true;

    if (!record.nombre || record.nombre.trim() === '') {
      this.validationErrors['nombre'] = 'El nombre es requerido';
      isValid = false;
    }

    if (!record.apellido || record.apellido.trim() === '') {
      this.validationErrors['apellido'] = 'El apellido es requerido';
      isValid = false;
    }

    if (!record.sexo || !['M', 'F'].includes(record.sexo)) {
      this.validationErrors['sexo'] = 'Debe seleccionar Masculino o Femenino';
      isValid = false;
    }

    // Gup XOR Dan
    const hasGup = record.gup !== null && record.gup !== undefined;
    const hasDan = record.dan !== null && record.dan !== undefined;

    if (!hasGup && !hasDan) {
      this.validationErrors['grado'] = 'Debe especificar una graduación';
      isValid = false;
    }

    // If DAN, certificate is required
    if (hasDan) {
      if (!record.nuCertDan || record.nuCertDan.trim() === '' || record.nuCertDan.trim().endsWith('-')) {
        this.validationErrors['nuCertDan'] = 'Debe completar el número de certificado';
        isValid = false;
      }
    }

    return isValid;
  }

  // ============================================================================
  // PAYMENT MANAGEMENT (unchanged)
  // ============================================================================

  loadPaymentHistory(): void {
    if (!this.itfRecord.id) return;

    this.itfDatabaseService.getHistorialPagos(this.itfRecord.id).subscribe(
      (history) => {
        this.paymentHistory = history;
      },
      (err) => {
        // Silent fail
      }
    );
  }

  togglePaymentHistory(): void {
    this.showPaymentHistory = !this.showPaymentHistory;
    if (this.showPaymentHistory && this.paymentHistory.length === 0) {
      this.loadPaymentHistory();
    }
  }

  openPaymentForm(): void {
    this.showPaymentModal = true;
    this.error = null;
  }

  closePaymentForm(): void {
    this.showPaymentModal = false;
  }

  onPaymentRegistered(pago: ItfPago): void {
    // Reload the ITF record to get updated payment info
    if (this.itfRecord.id) {
      this.itfDatabaseService.getById(this.itfRecord.id).subscribe(
        (updated) => {
          this.itfRecord = updated;
          this.loadPaymentHistory();
          this.successMessage = `Pago registrado exitosamente para el año ${pago.anio}`;
          setTimeout(() => this.successMessage = null, 3000);
        }
      );
    }
  }

  get paymentStatus(): { text: string; class: string; icon: string } {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth(); // 0-11

    // Check if there's a valid payment (current or future year)
    if (this.itfRecord.matriculaVigente) {
      // Payment exists for current or future year
      if (this.itfRecord.matriculaAnio === currentYear) {
        return {
          text: 'VIGENTE',
          class: 'status-vigente',
          icon: 'fa-check-circle'
        };
      } else if (this.itfRecord.matriculaAnio && this.itfRecord.matriculaAnio > currentYear) {
        // Payment is for a future year
        return {
          text: `VIGENTE (${this.itfRecord.matriculaAnio})`,
          class: 'status-vigente',
          icon: 'fa-check-circle'
        };
      }
    }

    // No current or future payment
    if (!this.itfRecord.matriculaAnio) {
      return {
        text: 'SIN PAGO',
        class: 'status-sin-pago',
        icon: 'fa-times-circle'
      };
    }

    // Payment is for current year, close to expiration
    if (this.itfRecord.matriculaAnio === currentYear && currentMonth >= 10) {
      return {
        text: 'PRÓXIMO A VENCER',
        class: 'status-proximo-vencer',
        icon: 'fa-exclamation-triangle'
      };
    }

    // Payment is for a past year
    return {
      text: 'VENCIDO',
      class: 'status-vencido',
      icon: 'fa-exclamation-circle'
    };
  }

  get canRegisterPayment(): boolean {
    return !this.isNew && !this.isEditMode && this.itfRecord.id !== null;
  }

  // ============================================================================
  // HELPERS
  // ============================================================================

  formatDate(date?: string | null): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('es-AR');
  }

  formatCurrency(amount?: number | null): string {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount);
  }
}